/**
 * IndexedDB-backed cache for match detail API responses.
 */

import {
  get as idbGet,
  set as idbSet,
  del as idbDel,
  keys as idbKeys,
} from 'idb-keyval';

const KEY_PREFIX = 'streampitch:';

export interface CacheEntry<T> {
  data: T;
  cachedAt: number;
}

export async function cacheGet<T>(key: string): Promise<CacheEntry<T> | null> {
  try {
    const value = (await idbGet(KEY_PREFIX + key)) as CacheEntry<T> | undefined;
    return value ?? null;
  } catch {
    // IDB can fail in private modes or when disabled. Treat as cache miss.
    return null;
  }
}

export async function cacheSet<T>(key: string, data: T): Promise<void> {
  try {
    await idbSet(KEY_PREFIX + key, { data, cachedAt: Date.now() } satisfies CacheEntry<T>);
  } catch {
    // Silently fail — the next visit will just refetch.
  }
}

export async function cacheBust(key: string): Promise<void> {
  try {
    await idbDel(KEY_PREFIX + key);
  } catch {
    // Ignore.
  }
}

/**
 * Wipe all StreamPitch keys. Used by the Settings page "Clear cache"
 * button. Only deletes keys with the `streampitch:` prefix so other
 * apps sharing the same IDB database are unaffected.
 */
export async function cacheClearAll(): Promise<void> {
  try {
    const allKeys = await idbKeys();
    const spKeys = allKeys.filter(
      (key) => typeof key === 'string' && key.startsWith(KEY_PREFIX)
    );
    await Promise.all(spKeys.map((key) => idbDel(key)));
  } catch {
    // Ignore.
  }
}

/**
 * Fetch JSON with a cache-first read and a background revalidation.
 *
 * • Returns the cached value immediately if it exists.
 * • In the background, fetches fresh and calls `onUpdate` if the
 *   data changed (cheap deep-equal by length + first/last id).
 * • If the cache is empty, awaits the network fetch.
 *
 * The `bypassCache` flag (used by the Refresh button) skips the
 * cache read entirely and forces a network fetch.
 */
export async function fetchJsonWithCache<T>(args: {
  url: string;
  cacheKey: string;
  bypassCache?: boolean;
  onUpdate?: (fresh: T) => void;
  isEqual?: (a: T, b: T) => boolean;
}): Promise<T> {
  const { url, cacheKey, bypassCache, onUpdate, isEqual } = args;

  const readCache = async (): Promise<{ value: T | null; hasCache: boolean }> => {
    if (bypassCache) return { value: null, hasCache: false };
    const entry = await cacheGet<T>(cacheKey);
    return { value: entry?.data ?? null, hasCache: !!entry };
  };

  const cached = await readCache();

  // Background revalidate.
  const networkFetch = (async () => {
    try {
      const response = await fetch(url);
      if (!response.ok) return;
      const fresh = (await response.json()) as T;
      await cacheSet(cacheKey, fresh);
      // Notify caller if the data actually changed (or if there was
      // no cache before — first run after deploy).
      if (onUpdate) {
        if (!cached.value) {
          onUpdate(fresh);
        } else if (!isEqual || !isEqual(cached.value, fresh)) {
          onUpdate(fresh);
        }
      }
    } catch {
      // Network failed. Caller already has the cache (or null).
    }
  })();

  if (cached.value !== null) {
    // Don't await the network fetch — let it run in the background.
    return cached.value;
  }
  // Cold start with no cache — wait for the network.
  await networkFetch;
  const afterFetch = await readCache();
  if (afterFetch.value !== null) return afterFetch.value;
  throw new Error(`Failed to fetch ${url} and no cache available`);
}

/**
 * Cheap structural comparison for revalidation. Compares by stringified
 * length + a few sample ids. Good enough for the channels list — if
 * the upstream added/removed channels we'll catch it without
 * deep-equalling every record.
 */
export function channelsChanged<T extends { id: string }>(a: T[], b: T[]): boolean {
  if (a.length !== b.length) return true;
  if (a[0]?.id !== b[0]?.id) return true;
  if (a[a.length - 1]?.id !== b[a.length - 1]?.id) return true;
  return false;
}


